import React, { useContext, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import ProductDescription from './ProductDescription/ProductDescription';
import ProductHelp from './ProductHelp/ProductHelp';
import ProductRSE from './ProductRSE/ProductRSE';
import ProductReviews from './ProductReviews/ProductReviews';
import ProductShipping from './ProductShipping/ProductShipping';
import ProductSizeGuide from './ProductSizeGuide/ProductSizeGuide';

import { tabContext } from 'contexts/tabContext';
import { SelectedProductAttributes } from 'types/ProductPageV2/Products';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import {
  ProductReviewsDetailsType,
  ProductReviewsRatingType,
} from 'types/ProductPageV2/ProductReviews';
import {
  DESCRIPTION_TAB_ID,
  HELP_TAB_ID,
  RATING_TAB_ID,
  RSE_TAB_ID,
  SHIPPING_TAB_ID,
  SIZE_GUIDE_TAB_ID,
} from 'settings/productV2';

import styles from './ProductTabMobile.module.scss';

/**
 * Product page of website. Contains details product data.
 */
function ProductTabDesktop({
  productAttributes,
  rating,
  detailsReviews,
}: {
  productAttributes: SelectedProductAttributes;
  rating: ProductReviewsRatingType | undefined;
  detailsReviews: ProductReviewsDetailsType[] | undefined;
}): JSX.Element {
  const { idTab, setIdTab, scrollToTabs, tabsRef } = useContext(tabContext);
  const { t } = useTranslation();
  const { sizeGuide } = productAttributes;
  const [oldTabsRef, setOldTabsRef] = useState<Element | null>(null);

  function handleChange(event: ChangeEvent<HTMLDivElement>, panel: number) {
    if (idTab === panel) {
      setIdTab(null);
    } else {
      setIdTab(panel);
      setOldTabsRef(tabsRef.current); // Set old value to reset after scroll
      tabsRef.current = event.target.offsetParent; // Scroll to element
    }
  }

  /**
   * Scroll down to the tab after opening the collapsible
   */
  const handleEndChangeTab = () => {
    scrollToTabs();
    tabsRef.current = oldTabsRef; // Reset tabsRef value
  };

  const sections = [
    {
      styleDetailRoot: styles.accordionDescriptionDetail,
      id: DESCRIPTION_TAB_ID,
      label: t('LABEL_TAB_DESCRIPTION'),
      gtm_label: t('LABEL_TAB_DESCRIPTION', { lng: 'fr' }),
      content: <ProductDescription productAttributes={productAttributes} />,
    },
    {
      id: SIZE_GUIDE_TAB_ID,
      label: t('LABEL_TAB_SIZE_GUIDE'),
      gtm_label: t('LABEL_TAB_SIZE_GUIDE', { lng: 'fr' }),
      content: <ProductSizeGuide sizeGuide={sizeGuide.Value} />,
    },
    {
      id: SHIPPING_TAB_ID,
      label: t('LABEL_TAB_SHIPPING'),
      gtm_label: t('LABEL_TAB_SHIPPING', { lng: 'fr' }),
      content: <ProductShipping />,
    },
    {
      id: RATING_TAB_ID,
      label: t('LABEL_TAB_RATING'),
      gtm_label: t('LABEL_TAB_RATING', { lng: 'fr' }),
      content: (
        <ProductReviews rating={rating} detailsReviews={detailsReviews} />
      ),
    },
    {
      id: RSE_TAB_ID,
      label: t('LABEL_TAB_RSE'),
      gtm_label: t('LABEL_TAB_RSE', { lng: 'fr' }),
      content: <ProductRSE htmlContent={productAttributes?.RSE?.Value} />,
    },
    {
      id: HELP_TAB_ID,
      label: t('LABEL_TAB_HELP'),
      gtm_label: t('LABEL_TAB_HELP', { lng: 'fr' }),
      content: <ProductHelp />,
    },
  ];

  return (
    <div className={styles.accordionContent}>
      {sections.map(section => {
        const { id, label, content, gtm_label, styleDetailRoot } = section;
        return (
          <MuiAccordion
            key={id}
            classes={{
              root: styles.accordionRoot,
              expanded: styles.accordionRootExpanded,
            }}
            square
            expanded={idTab?.toString() === id.toString()}
            onChange={event =>
              handleChange(event as ChangeEvent<HTMLDivElement>, id)
            }
            elevation={0}
            TransitionProps={{
              timeout: 0,
              onExited: handleEndChangeTab,
            }}
          >
            <MuiAccordionSummary
              classes={{
                root: styles.accordionSummaryRoot,
                content: styles.accordionSummaryContent,
                expanded: styles.accordionSummaryExpanded,
                expandIcon: styles.expandIcon,
              }}
              aria-controls={`${id}-content`}
              id={`${id}-header`}
              expandIcon={<ExpandMoreIcon />}
              onClick={() => {
                pushGtmEvent({
                  event: '_click_other',
                  category: 'clic autre - fiche produit',
                  element: gtm_label,
                });
              }}
            >
              {id === HELP_TAB_ID && <>{label}</>}
              {id !== HELP_TAB_ID && <h2>{label}</h2>}
            </MuiAccordionSummary>
            <MuiAccordionDetails
              classes={{
                root: classNames(styles.accordionDetail, styleDetailRoot),
              }}
            >
              {content}
            </MuiAccordionDetails>
          </MuiAccordion>
        );
      })}
    </div>
  );
}

export default ProductTabDesktop;
